import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ActivityIndicator, Share } from 'react-native'

import { publishEvent } from '@zet/analytics'
import LazyHOC from '@zet/common/components/lazyHoc'
import { B2, BTN_SM, colors as zetColors } from '@zet/design-system'
import { Icon } from '@zet/design-system/icons'
import { Stack, XStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import { SH3 } from '@zet/design-system/typography'
import _ from 'lodash'
import { ModalComponentProp } from 'react-native-modalfy'
import YoutubePlayer, { YoutubeIframeRef } from 'react-native-youtube-iframe'

import { availableLanguages, i18n } from '../../localization'
import { DEVICE_HEIGHT, DEVICE_WIDTH, appVideos } from '../../utils/Constants'
import { ModalStackParams } from '../modalConfig'

type Props = ModalComponentProp<ModalStackParams, void, 'YoutubeVideoModal'>

export default function YoutubeVideoModal({
  modal: { closeModal, getParam },
}: Props) {
  const title = getParam('title', 'Video')
  const videoId = getParam('videoId', 'zetPlaceHolder')
  const videoData = getParam('videoData', null)
  let onCloseModal = getParam('closeCallback', () => {})
  const isLanguageSelect = getParam('showLanguage', true)
  const isShareEnabled = getParam('isShareEnabled', false)
  const [playing, setPlaying] = useState<boolean>(true)
  const playerRef = useRef<YoutubeIframeRef>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedLocale, setSelectedLocale] = useState('en')
  const [selectedVideo, setSelectedVideo] = useState({})

  const onShare = async (videoId?: string) => {
    try {
      await Share.share({
        message: `https://www.youtube.com/watch?v=${videoId}`,
      })
    } catch (error) {
      console.warn(error)
    }
  }

  const onStateChange = useCallback((state: string) => {
    if (state === 'ended') {
      setPlaying(false)
    }
  }, [])

  useEffect(() => {
    publishEvent('screen_view', { screen_name: 'YoutubeVideoModal' })
  }, [])

  const onDismiss = () => {
    closeModal()
  }

  useEffect(() => {
    if (selectedLocale && !_.isEmpty(videoData)) {
      if (
        Object.keys(videoData).length === 1 &&
        Object.keys(videoData).includes('hi')
      ) {
        setSelectedVideo(videoData?.hi)
      } else {
        setSelectedVideo(videoData[selectedLocale])
      }
    } else if (_.isEmpty(videoData) && videoId) {
      setSelectedVideo({ videoId: videoId || appVideos.zetPlaceHolder.videoId })
    }
  }, [videoId, selectedLocale, videoData])
  return (
    <LazyHOC>
      <Stack className="rounded bg-white " style={{ width: DEVICE_WIDTH - 32 }}>
        <XStack className="border-grays-30 border-b-2 px-3 py-2">
          <Icon type="VideoOutlined" />
          <SH3 className="mx-2 flex-grow">{title}</SH3>
          <Pressable onPress={onDismiss}>
            <Icon type="Close" />
          </Pressable>
        </XStack>
        <Stack className="p-1">
          {isLoading && (
            <ActivityIndicator
              size={'large'}
              color={zetColors.primary[100]}
              style={{ position: 'absolute', alignSelf: 'center', top: 180 }}
            />
          )}
          {selectedVideo?.videoId || selectedVideo?.videoLink ? (
            <YoutubePlayer
              webViewStyle={{
                justifyContent: 'flex-end',
              }}
              ref={playerRef}
              height={DEVICE_HEIGHT / 1.5}
              play={playing}
              videoId={selectedVideo.videoId || selectedVideo?.videoLink}
              onChangeState={onStateChange}
              forceAndroidAutoplay
              webViewProps={{
                containerStyle: { borderRadius: 5 },
                onLoad: () => {
                  setIsLoading(false)
                },
                onLoadStart: () => {
                  setIsLoading(true)
                },
                injectedJavaScript: `
                        var element = document.getElementsByClassName('container')[0];
                        element.style.position = 'unset';
                        element.style.paddingBottom = 'unset';
                        true;
                      `,
              }}
            />
          ) : (
            <></>
          )}
        </Stack>
        {isLanguageSelect &&
        !_.isEmpty(videoData) &&
        Object.keys(videoData).length === 2 ? (
          <Stack className="p-3">
            <SH3 className="text-grays-60">{i18n.t('videoPopup')}</SH3>
            <XStack className="mt-4 w-auto items-center px-2">
              {availableLanguages.map((language, index) => (
                <Pressable
                  opacity
                  onPress={() => setSelectedLocale(language.code)}
                  eventName="click_action"
                  eventPayload={{
                    item_name: `youtube_video_modal_language_select_${language.code}`,
                  }}
                  key={JSON.stringify(language) + index}
                >
                  <XStack className="ml-2 items-center px-2">
                    <Icon
                      type={
                        language.code === selectedLocale
                          ? 'RadioOn'
                          : 'RadioOff'
                      }
                      color={zetColors.primary[50]}
                      size={22}
                    />
                    <B2 className="ml-2">{language.name}</B2>
                  </XStack>
                </Pressable>
              ))}
            </XStack>
          </Stack>
        ) : (
          <></>
        )}
        {isShareEnabled && (
          <Pressable
            key={String(isShareEnabled)}
            onPress={() =>
              onShare(selectedVideo?.videoId || selectedVideo?.videoLink)
            }
            opacity
            className="border-t-grays-30 h-8 w-[100%] items-center justify-center border-t"
            eventName="click_action"
            eventPayload={{ item_name: 'youtube_video_modal_share' }}
          >
            <XStack>
              <Icon type="Share" color={zetColors.primary[100]} size={22} />
              <BTN_SM className="text-primary-100 ml-2">Share Video</BTN_SM>
            </XStack>
          </Pressable>
        )}
      </Stack>
    </LazyHOC>
  )
}
